import { useState, useEffect } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { Plus, CalendarCheck, Clock, CheckCircle2, XCircle, Edit3, Trash2 } from "lucide-react";

export default function VisitsPage() {
  const [visits, setVisits] = useState([]);
  const [leads, setLeads] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editVisit, setEditVisit] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchData = async () => {
    try {
      const [v, l, p] = await Promise.all([
        api.get("/visits", { params: statusFilter !== "all" ? { status: statusFilter } : {} }),
        api.get("/leads"),
        api.get("/properties")
      ]);
      setVisits(v.data);
      setLeads(l.data);
      setProperties(p.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [statusFilter]);

  const handleCreate = async (form) => {
    try {
      await api.post("/visits", form);
      setShowCreate(false);
      fetchData();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await api.put(`/visits/${id}`, { status });
      fetchData();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this visit?")) return;
    try {
      await api.delete(`/visits/${id}`);
      fetchData();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4" data-testid="visits-page">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-9 px-3 border border-border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm"
          data-testid="visits-status-filter"
        >
          <option value="all">All Visits</option>
          <option value="scheduled">Scheduled</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <button
          onClick={() => setShowCreate(true)}
          className="h-9 px-4 bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#002277] transition-colors flex items-center gap-2 rounded-sm"
          data-testid="create-visit-button"
        >
          <Plus size={14} /> Schedule Visit
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-[#868E96] py-8 text-center">Loading...</p>
      ) : visits.length === 0 ? (
        <div className="border border-border bg-white p-12 text-center">
          <CalendarCheck size={32} className="mx-auto mb-3 text-[#868E96]" />
          <p className="text-sm text-[#868E96]">No visits scheduled yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {visits.map((visit, i) => (
            <div key={visit.id} className="border border-border bg-white p-4 hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200 animate-fade-in" style={{ animationDelay: `${i * 0.03}s` }} data-testid={`visit-card-${visit.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-bold text-[#0A0A0A]" style={{ fontFamily: 'Chivo' }}>{visit.lead_name}</h4>
                  <p className="text-xs text-[#868E96]">{visit.property_title}</p>
                </div>
                <span className={`status-badge status-${visit.status}`}>{visit.status}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-[#495057] mb-2">
                <Clock size={14} />
                <span>{visit.scheduled_at ? new Date(visit.scheduled_at).toLocaleString() : "TBD"}</span>
              </div>
              <p className="text-xs text-[#868E96] mb-1">Agent: {visit.agent_name}</p>
              {visit.notes && <p className="text-xs text-[#495057] mb-3">{visit.notes}</p>}
              <div className="flex items-center gap-1 pt-3 border-t border-border">
                {visit.status === "scheduled" && (
                  <>
                    <button onClick={() => handleStatusChange(visit.id, "completed")} className="p-1.5 hover:bg-green-50 rounded-sm" title="Complete" data-testid={`complete-visit-${visit.id}`}><CheckCircle2 size={14} className="text-[#2B8A3E]" /></button>
                    <button onClick={() => handleStatusChange(visit.id, "cancelled")} className="p-1.5 hover:bg-red-50 rounded-sm" title="Cancel" data-testid={`cancel-visit-${visit.id}`}><XCircle size={14} className="text-[#C92A2A]" /></button>
                  </>
                )}
                <button onClick={() => handleDelete(visit.id)} className="p-1.5 hover:bg-red-50 rounded-sm" title="Delete" data-testid={`delete-visit-${visit.id}`}><Trash2 size={14} className="text-[#C92A2A]" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <VisitFormModal leads={leads} properties={properties} onSave={handleCreate} onClose={() => setShowCreate(false)} />
      )}
    </div>
  );
}

function VisitFormModal({ leads, properties, onSave, onClose }) {
  const [form, setForm] = useState({
    lead_id: "",
    property_id: "",
    scheduled_at: "",
    notes: "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.lead_id || !form.property_id || !form.scheduled_at) {
      alert("Please fill all required fields");
      return;
    }
    onSave(form);
  };

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white border border-border p-6 w-[28rem]" onClick={e => e.stopPropagation()} data-testid="visit-form-modal">
        <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'Chivo' }}>Schedule Visit</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#868E96] block mb-1">Lead *</label>
            <select value={form.lead_id} onChange={e => setForm({...form, lead_id: e.target.value})} className="w-full h-9 px-3 border border-border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm" required data-testid="visit-lead-select">
              <option value="">Select a lead</option>
              {leads.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#868E96] block mb-1">Property *</label>
            <select value={form.property_id} onChange={e => setForm({...form, property_id: e.target.value})} className="w-full h-9 px-3 border border-border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm" required data-testid="visit-property-select">
              <option value="">Select a property</option>
              {properties.map(p => <option key={p.id} value={p.id}>{p.title} - ${p.price?.toLocaleString()}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#868E96] block mb-1">Date & Time *</label>
            <input type="datetime-local" value={form.scheduled_at} onChange={e => setForm({...form, scheduled_at: e.target.value})} className="w-full h-9 px-3 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm" required data-testid="visit-datetime-input" />
          </div>
          <div>
            <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#868E96] block mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="w-full h-20 p-3 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] rounded-sm resize-none" data-testid="visit-notes-input" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="h-9 px-4 border border-border text-sm rounded-sm hover:bg-[#F1F3F5]" data-testid="visit-form-cancel">Cancel</button>
            <button type="submit" className="h-9 px-4 bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#002277] rounded-sm" data-testid="visit-form-submit">Schedule</button>
          </div>
        </form>
      </div>
    </div>
  );
}
